import React from 'react';
import { 
  Save, X, Upload, Camera, MapPin, Globe, Lock, Eye, EyeOff,
  User, MessageSquare, Leaf, Calendar, Award, Sparkles, Star,
  Check, AlertCircle, Plus, Minus, Search, ChevronDown
} from 'lucide-react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Container,
  Paper,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Grid,
  Snackbar,
  Alert,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormHelperText,
  LinearProgress,
} from '@mui/material';
import {
    Info as InfoIcon,
    PhotoCamera as PhotoCameraIcon,
    LocationOn as LocationOnIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
  } from '@mui/icons-material';
  
import API_URL from '../config/ApiUrl';

const instructions = {
    handle: 'How others see you, use your username if you want to.',
  };

class EditProfile extends React.Component {
  
  constructor(props) {
    super(props);
    
    const { formData = {} } = props;
    
    this.state = {
      // Form data
      displayName: formData.displayName || '',
      bio: formData.bio || '',
      location: formData.location || '',
      website: formData.website || '',
      favoriteStrains: formData.favoriteStrains || [],
      consumptionMethods: formData.consumptionMethods || [],
      toleranceLevel: formData.toleranceLevel || 'medium',
      yearsOfExperience: formData.yearsOfExperience || 1,
      medicalUser: formData.medicalUser || false,
      isPublic: formData.isPublic !== false,
      showLocation: formData.showLocation !== false,
      showMedicalStatus: formData.showMedicalStatus !== false,
      showConsumptionMethods: formData.showConsumptionMethods !== false,
      
      // UI state
      activeSection: 'basic',
      loading: false,
      errors: {},
      
      // Tag selector states
      strainSearchTerm: '',
      strainDropdownOpen: false,
      methodSearchTerm: '',
      methodDropdownOpen: false,
      
      // Input focus states
      focusedInput: null,
      
      // Mobile state
      isMobile: window.innerWidth <= 768
    };

    this.strainOptions = [
      "Blue Dream", "Sour Diesel", "Girl Scout Cookies", "OG Kush", "White Widow",
      "AK-47", "Purple Haze", "Jack Herer", "Granddaddy Purple", "Northern Lights",
      "Green Crack", "Pineapple Express", "Strawberry Cough", "Afghani", "Bubba Kush"
    ];

    this.consumptionOptions = [
      "Joints", "Blunts", "Pipes", "Bongs", "Vaporizer", "Edibles", 
      "Tinctures", "Topicals", "Dabs", "Concentrates"
    ];

    this.toleranceLevels = [
      { value: "low", label: "Low - New to cannabis", color: "#10b981" },
      { value: "medium", label: "Medium - Regular user", color: "#f59e0b" },
      { value: "high", label: "High - Daily user", color: "#ef4444" }
    ];

    this.sections = [
      { id: 'basic', label: 'Basic Info', icon: <User size={16} />, color: '#00d4aa' },
      { id: 'cannabis', label: 'Cannabis Profile', icon: <Leaf size={16} />, color: '#10b981' },
      { id: 'privacy', label: 'Privacy', icon: <Lock size={16} />, color: '#8b5cf6' }
    ];
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.setState({ isMobile: window.innerWidth <= 768 });
  }
  

  // DIRECT STATE UPDATES - NO COMPLEX LOGIC
  updateField = (field, value) => {
    this.setState({ 
      [field]: value,
      errors: { ...this.state.errors, [field]: null }
    });
  }

  setActiveSection = (section) => {
    this.setState({ activeSection: section });
  }

  setFocusedInput = (inputName) => {
    this.setState({ focusedInput: inputName });
  }

  addToArray = (field, value) => {
    if (value && !this.state[field].includes(value)) {
      this.setState({
        [field]: [...this.state[field], value]
      });
    }
  }

  removeFromArray = (field, value) => {
    this.setState({
      [field]: this.state[field].filter(item => item !== value)
    });
  }

  updateSearchTerm = (field, value) => {
    this.setState({ [field]: value });
  }

  setDropdownOpen = (field, isOpen) => {
    this.setState({ [field]: isOpen });
  }

  validateForm = () => {
    const { displayName, bio, favoriteStrains, consumptionMethods } = this.state;
    const errors = {};
    
    if (!displayName?.trim()) {
      errors.displayName = "Display name is required";
    }
    
    if (!bio?.trim()) {
      errors.bio = "Bio is required";
    } else if (bio.length > 500) {
      errors.bio = "Bio must be under 500 characters";
    }
    
    if (favoriteStrains.length === 0) {
      errors.favoriteStrains = "Please select at least one favorite strain";
    }
    
    if (consumptionMethods.length === 0) {
      errors.consumptionMethods = "Please select at least one consumption method";
    }
    
    this.setState({ errors });
    return Object.keys(errors).length === 0;
  }

  handleSave = async () => {
    if (!this.validateForm()) return;
    
    this.setState({ loading: true });
    
    try {
      const apiUrl = `${API_URL}/api/profile`;
      const method = 'PUT';
      
      const response = await fetch(apiUrl, {
        method: method,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: this.state.displayName,
          bio: this.state.bio,
          location: this.state.location,
          website: this.state.website,
          favorite_strains: this.state.favoriteStrains,
          consumption_methods: this.state.consumptionMethods,
          tolerance_level: this.state.toleranceLevel,
          years_of_experience: this.state.yearsOfExperience,
          medical_user: this.state.medicalUser,
          is_public: this.state.isPublic,
          show_location: this.state.showLocation,
          show_medical_status: this.state.showMedicalStatus,
          show_consumption_methods: this.state.showConsumptionMethods
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(result.message || 'Profile saved successfully!');
        if (this.props.onSaveSuccess) {
          this.props.onSaveSuccess(result.data);
        }
      } else {
        if (result.error) {
          alert(`Error: ${result.error}`);
        }
      }
      
    } catch (error) {
      console.error('Failed to save profile:', error);
      alert("Failed to save profile. Please try again.");
    } finally {
      this.setState({ loading: false });
    }
  }

  renderDirectInput = ({ 
    label, 
    field,
    type = 'text', 
    placeholder, 
    icon: Icon,
    required = false,
    ...props 
  }) => {
    const { errors, focusedInput, isMobile } = this.state;
    const value = this.state[field];
    const error = errors[field];
    const isFocused = focusedInput === field;
    const hasValue = value && value.toString().length > 0;

    return (
      <div style={{ position: 'relative', marginBottom: isMobile ? '20px' : '24px' }}>
        <div style={{
          position: 'relative',
          background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
          border: `2px solid ${error ? '#ef4444' : isFocused ? '#00d4aa' : '#374151'}`,
          borderRadius: isMobile ? '12px' : '16px',
          transition: 'all 0.3s ease',
          overflow: 'hidden'
        }}>
          {Icon && (
            <div style={{
              position: 'absolute',
              left: isMobile ? '12px' : '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: isFocused ? '#00d4aa' : '#6b7280',
              transition: 'color 0.3s ease',
              zIndex: 2
            }}>
              <Icon size={isMobile ? 16 : 18} />
            </div>
          )}
          
          <input
            type={type}
            value={value}
            onChange={(e) => this.updateField(field, e.target.value)}
            onFocus={() => this.setFocusedInput(field)}
            onBlur={() => this.setFocusedInput(null)}
            placeholder=" "
            style={{
              width: '100%',
              padding: isMobile 
                ? (Icon ? '24px 12px 10px 40px' : '24px 12px 10px 12px')
                : (Icon ? '28px 16px 12px 48px' : '28px 16px 12px 16px'),
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#f9fafb',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '500'
            }}
            {...props}
          />
          
          <label style={{
            position: 'absolute',
            left: Icon ? (isMobile ? '40px' : '48px') : (isMobile ? '12px' : '16px'),
            top: isFocused || hasValue ? '6px' : '50%',
            transform: isFocused || hasValue ? 'translateY(0)' : 'translateY(-50%)',
            fontSize: isFocused || hasValue ? '10px' : (isMobile ? '14px' : '16px'),
            fontWeight: '600',
            color: isFocused ? '#00d4aa' : error ? '#ef4444' : '#9ca3af',
            transition: 'all 0.3s ease',
            pointerEvents: 'none',
            background: isFocused || hasValue ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)' : 'transparent',
            padding: isFocused || hasValue ? '0 4px' : '0',
            zIndex: 1
          }}>
            {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
          </label>
        </div>
        
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            color: '#ef4444',
            fontSize: isMobile ? '11px' : '12px',
            fontWeight: '500'
          }}>
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    );
  }

  renderDirectTextarea = ({ label, field, placeholder, rows = 4 }) => {
    const { errors, isMobile } = this.state;
    const value = this.state[field];
    const error = errors[field];

    return (
      <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: isMobile ? '8px' : '12px'
        }}>
          <MessageSquare size={isMobile ? 14 : 16} style={{ color: '#00d4aa' }} />
          <label style={{
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '700',
            color: '#f9fafb',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {label} <span style={{ color: '#ef4444' }}>*</span>
          </label>
        </div>
        
        <div style={{ position: 'relative' }}>
          <textarea
            value={value}
            onChange={(e) => this.updateField(field, e.target.value)}
            placeholder={placeholder}
            rows={isMobile ? 3 : rows}
            style={{
              width: '100%',
              padding: isMobile ? '16px' : '20px',
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              border: `2px solid ${error ? '#ef4444' : '#374151'}`,
              borderRadius: isMobile ? '12px' : '16px',
              color: '#f9fafb',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '500',
              resize: 'vertical',
              fontFamily: 'inherit',
              outline: 'none',
              transition: 'border-color 0.3s ease'
            }}
          />
          <div style={{
            position: 'absolute',
            bottom: isMobile ? '8px' : '12px',
            right: isMobile ? '16px' : '20px',
            fontSize: isMobile ? '10px' : '12px',
            color: value.length > 450 ? '#ef4444' : '#6b7280',
            fontWeight: '600'
          }}>
            {value.length}/500
          </div>
        </div>
        
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            color: '#ef4444',
            fontSize: isMobile ? '11px' : '12px',
            fontWeight: '500'
          }}>
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    );
  }

  renderTagSelector = ({ 
    label, 
    options, 
    selectedField,
    searchField,
    dropdownField,
    placeholder, 
    icon: Icon 
  }) => {
    const { errors, isMobile } = this.state;
    const selected = this.state[selectedField];
    const searchTerm = this.state[searchField];
    const isOpen = this.state[dropdownField];
    const error = errors[selectedField];
    
    const filteredOptions = options.filter(option => 
      !selected.includes(option) && 
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: isMobile ? '8px' : '12px'
        }}>
          {Icon && <Icon size={isMobile ? 14 : 16} style={{ color: '#00d4aa' }} />}
          <label style={{
            fontSize: isMobile ? '12px' : '14px',
            fontWeight: '700',
            color: '#f9fafb',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {label}
          </label>
        </div>

        {/* Selected Tags */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? '6px' : '8px',
          marginBottom: isMobile ? '8px' : '12px',
          minHeight: isMobile ? '32px' : '40px',
          padding: isMobile ? '6px' : '8px',
          background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
          borderRadius: isMobile ? '8px' : '12px',
          border: '1px solid #374151'
        }}>
          {selected.length === 0 ? (
            <span style={{
              color: '#6b7280',
              fontSize: isMobile ? '12px' : '14px',
              fontStyle: 'italic',
              display: 'flex',
              alignItems: 'center'
            }}>
              No {label.toLowerCase()} selected
            </span>
          ) : (
            selected.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: isMobile ? '4px 8px' : '6px 12px',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                borderRadius: isMobile ? '16px' : '20px',
                fontSize: isMobile ? '10px' : '12px',
                fontWeight: '600',
                color: 'white',
                boxShadow: '0 2px 8px rgba(0, 212, 170, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 212, 170, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 212, 170, 0.3)';
              }}
              >
                <Sparkles size={isMobile ? 10 : 12} />
                {item}
                <button
                  onClick={() => this.removeFromArray(selectedField, item)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: isMobile ? '14px' : '16px',
                    height: isMobile ? '14px' : '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  <X size={isMobile ? 8 : 10} />
                </button>
              </div>
            ))
          )}
        </div>

        {/* Search Input */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => this.updateSearchTerm(searchField, e.target.value)}
            onFocus={() => this.setDropdownOpen(dropdownField, true)}
            onBlur={() => setTimeout(() => this.setDropdownOpen(dropdownField, false), 200)}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: isMobile ? '12px 40px 12px 12px' : '16px 48px 16px 16px',
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              border: `2px solid ${error ? '#ef4444' : '#374151'}`,
              borderRadius: isMobile ? '8px' : '12px',
              color: '#f9fafb',
              fontSize: isMobile ? '12px' : '14px',
              outline: 'none',
              transition: 'border-color 0.3s ease'
            }}
          />
          <Search 
            size={isMobile ? 16 : 18} 
            style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }}
          />

          {/* Dropdown */}
          {isOpen && filteredOptions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
              border: '2px solid #374151',
              borderTop: 'none',
              borderRadius: isMobile ? '0 0 8px 8px' : '0 0 12px 12px',
              maxHeight: isMobile ? '150px' : '200px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
            }}>
              {filteredOptions.map((option, index) => (
                <button
                  key={index}
                  onClick={() => {
                    this.addToArray(selectedField, option);
                    this.updateSearchTerm(searchField, '');
                  }}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px 12px' : '12px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#f9fafb',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: isMobile ? '12px' : '14px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(0, 212, 170, 0.1)';
                    e.target.style.color = '#00d4aa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'none';
                    e.target.style.color = '#f9fafb';
                  }}
                >
                  <Plus size={isMobile ? 12 : 14} />
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginTop: '8px',
            color: '#ef4444',
            fontSize: isMobile ? '11px' : '12px',
            fontWeight: '500'
          }}>
            <AlertCircle size={12} />
            {error}
          </div>
        )}
      </div>
    );
  }

  renderSectionNav = () => {
    const { activeSection, isMobile } = this.state;
    
    return (
      <div style={{
        display: 'flex',
        gap: isMobile ? '2px' : '4px',
        marginBottom: isMobile ? '20px' : '32px',
        background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
        padding: isMobile ? '4px' : '6px',
        borderRadius: isMobile ? '12px' : '16px',
        border: '1px solid #374151'
      }}>
        {this.sections.map((section) => (
          <button
            key={section.id}
            onClick={() => this.setActiveSection(section.id)}
            style={{
              flex: 1,
              padding: isMobile ? '8px 4px' : '12px 16px',
              background: activeSection === section.id 
                ? `linear-gradient(135deg, ${section.color} 0%, ${section.color}dd 100%)`
                : 'transparent',
              border: 'none',
              borderRadius: isMobile ? '8px' : '12px',
              color: activeSection === section.id ? 'white' : '#9ca3af',
              fontSize: isMobile ? '10px' : '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: isMobile ? '4px' : '8px',
              transform: activeSection === section.id ? 'scale(1.02)' : 'scale(1)',
              boxShadow: activeSection === section.id 
                ? `0 4px 16px ${section.color}40` 
                : 'none',
              flexDirection: isMobile ? 'column' : 'row'
            }}
          >
            {React.cloneElement(section.icon, { size: isMobile ? 12 : 16 })}
            <span style={{ textAlign: 'center' }}>
              {isMobile ? section.label.split(' ')[0] : section.label}
            </span>
          </button>
        ))}
      </div>
    );
  }

  render() {
    const { 
      activeSection, 
      toleranceLevel, 
      yearsOfExperience, 
      medicalUser,
      isPublic,
      showLocation,
      showMedicalStatus,
      showConsumptionMethods,
      loading,
      isMobile
    } = this.state;
    
    const { isMockProfile, handleCancel } = this.props;

    return (
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: isMobile ? '16px' : '24px',
        padding: '0',
        border: '1px solid #334155',
        marginBottom: isMobile ? '16px' : '24px',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        maxWidth: '100%',
        margin: isMobile ? '0 auto' : '0 auto'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
          padding: isMobile ? '20px 16px' : '32px',
          borderBottom: '1px solid #475569',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(0, 212, 170, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(139, 92, 246, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }} />
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            zIndex: 1
          }}>
            <div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: isMobile ? '20px' : '28px',
                fontWeight: '800',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 50%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {isMockProfile ? '✨ Complete Your Profile' : '🎨 Edit Profile'}
              </h1>
              <p style={{
                margin: 0,
                fontSize: isMobile ? '12px' : '16px',
                color: '#94a3b8',
                fontWeight: '500'
              }}>
                {isMockProfile ? 
                  'Create your cannabis community presence' : 
                  'Update your information and preferences'
                }
              </p>
            </div>
            
            {!isMockProfile && handleCancel && (
              <button
                onClick={handleCancel}
                style={{
                  padding: isMobile ? '8px' : '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '2px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: isMobile ? '8px' : '12px',
                  color: '#ef4444',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.target.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.target.style.transform = 'scale(1)';
                }}
              >
                <X size={isMobile ? 16 : 20} />
              </button>
            )}
          </div>

          {isMockProfile && (
            <div style={{
              marginTop: isMobile ? '12px' : '20px',
              padding: isMobile ? '12px 16px' : '16px 20px',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: isMobile ? '12px' : '16px',
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              position: 'relative',
              zIndex: 1
            }}>
              <div style={{
                padding: isMobile ? '6px' : '8px',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                borderRadius: '8px',
                fontSize: isMobile ? '12px' : '16px'
              }}>
                🎭
              </div>
              <div>
                <div style={{
                  color: '#f8fafc',
                  fontSize: isMobile ? '12px' : '14px',
                  fontWeight: '600',
                  marginBottom: '2px'
                }}>
                  Preview Mode Active
                </div>
                <div style={{
                  color: '#cbd5e1',
                  fontSize: isMobile ? '10px' : '12px'
                }}>
                  This is sample data. Complete your profile to join the community!
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: isMobile ? '16px' : '32px' }}>
          {this.renderSectionNav()}
          
          <div style={{
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: isMobile ? '16px' : '20px',
            padding: isMobile ? '20px' : '32px',
            border: '1px solid #334155',
            minHeight: isMobile ? '300px' : '400px'
          }}>
            {/* Basic Info Section */}
            {activeSection === 'basic' && (
              <div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '16px' : '24px',
                  marginBottom: isMobile ? '20px' : '32px',
                }}>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: '8px'
                  }}>
                    {this.renderDirectInput({
                      label: "Handle",
                      field: "displayName",
                      placeholder: "How should others see you?",
                      icon: User,
                      required: true
                    })}
                    <Tooltip title={instructions.handle} arrow>
                      <IconButton>
                        <InfoIcon sx={{ color: '#EF6191' }} />
                      </IconButton>
                    </Tooltip>
                  </div>

                  {this.renderDirectInput({
                    label: "Location",
                    field: "location",
                    placeholder: "City, State, Country",
                    icon: MapPin
                  })}
                </div>

                {this.renderDirectTextarea({
                  label: "Bio",
                  field: "bio",
                  placeholder: "Tell the community about your cannabis journey..."
                })}

                {this.renderDirectInput({
                  label: "Website",
                  field: "website",
                  placeholder: "https://your-website.com",
                  type: "url",
                  icon: Globe
                })}
              </div>
            )}

            {/* Cannabis Section */}
            {activeSection === 'cannabis' && (
              <div>
                {this.renderTagSelector({
                  label: "Favorite Strains",
                  options: this.strainOptions,
                  selectedField: "favoriteStrains",
                  searchField: "strainSearchTerm",
                  dropdownField: "strainDropdownOpen",
                  placeholder: "Search and add your favorite strains...",
                  icon: Sparkles
                })}

                {this.renderTagSelector({
                  label: "Consumption Methods",
                  options: this.consumptionOptions,
                  selectedField: "consumptionMethods",
                  searchField: "methodSearchTerm",
                  dropdownField: "methodDropdownOpen",
                  placeholder: "How do you prefer to consume?",
                  icon: Leaf
                })}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: isMobile ? '16px' : '24px',
                  marginBottom: isMobile ? '20px' : '24px'
                }}>
                  <div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: isMobile ? '8px' : '12px'
                    }}>
                      <Award size={isMobile ? 14 : 16} style={{ color: '#00d4aa' }} />
                      <label style={{
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: '700',
                        color: '#f9fafb',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Tolerance Level
                      </label>
                    </div>
                    
                    <div style={{
                      background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                      border: '2px solid #374151',
                      borderRadius: isMobile ? '12px' : '16px',
                      overflow: 'hidden'
                    }}>
                      {this.toleranceLevels.map((level) => (
                        <label
                          key={level.value}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: isMobile ? '12px 16px' : '16px 20px',
                            cursor: 'pointer',
                            background: toleranceLevel === level.value 
                              ? `linear-gradient(135deg, ${level.color}20 0%, ${level.color}10 100%)`
                              : 'transparent',
                            borderLeft: toleranceLevel === level.value 
                              ? `4px solid ${level.color}` 
                              : '4px solid transparent',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (toleranceLevel !== level.value) {
                              e.currentTarget.style.background = `${level.color}10`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (toleranceLevel !== level.value) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          <input
                            type="radio"
                            name="toleranceLevel"
                            value={level.value}
                            checked={toleranceLevel === level.value}
                            onChange={(e) => this.updateField('toleranceLevel', e.target.value)}
                            style={{ display: 'none' }}
                          />
                          <div style={{
                            width: isMobile ? '16px' : '20px',
                            height: isMobile ? '16px' : '20px',
                            borderRadius: '50%',
                            border: `2px solid ${level.color}`,
                            marginRight: isMobile ? '8px' : '12px',
                            position: 'relative',
                            transition: 'all 0.3s ease'
                          }}>
                            {toleranceLevel === level.value && (
                              <div style={{
                                width: isMobile ? '8px' : '10px',
                                height: isMobile ? '8px' : '10px',
                                borderRadius: '50%',
                                background: level.color,
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                              }} />
                            )}
                          </div>
                          <span style={{
                            color: '#f9fafb',
                            fontSize: isMobile ? '12px' : '14px',
                            fontWeight: '500'
                          }}>
                            {isMobile ? level.label.split(' - ')[0] : level.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    {this.renderDirectInput({
                      label: "Years of Experience",
                      field: "yearsOfExperience",
                      type: "number",
                      min: "0",
                      max: "50",
                      icon: Calendar
                    })}

                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? '8px' : '12px',
                      marginTop: isMobile ? '16px' : '24px',
                      padding: isMobile ? '16px' : '20px',
                      background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                      border: '2px solid #374151',
                      borderRadius: isMobile ? '12px' : '16px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onClick={() => this.updateField('medicalUser', !medicalUser)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#10b981';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#374151';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    >
                      <div style={{
                        width: isMobile ? '20px' : '24px',
                        height: isMobile ? '20px' : '24px',
                        borderRadius: '6px',
                        border: `2px solid ${medicalUser ? '#10b981' : '#6b7280'}`,
                        background: medicalUser 
                          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.3s ease'
                      }}>
                        {medicalUser && <Check size={isMobile ? 12 : 14} style={{ color: 'white' }} />}
                      </div>
                      <div>
                        <div style={{
                          color: '#f9fafb',
                          fontSize: isMobile ? '12px' : '14px',
                          fontWeight: '600'
                        }}>
                          Medical User
                        </div>
                        <div style={{
                          color: '#9ca3af',
                          fontSize: isMobile ? '10px' : '12px'
                        }}>
                          I use cannabis for medical purposes
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Section */}
            {activeSection === 'privacy' && (
              <div>
                <div style={{
                  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                  border: '2px solid #374151',
                  borderRadius: isMobile ? '16px' : '20px',
                  padding: isMobile ? '16px' : '24px',
                  marginBottom: isMobile ? '16px' : '24px'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '12px',
                    marginBottom: isMobile ? '16px' : '20px'
                  }}>
                    <div style={{
                      padding: isMobile ? '6px' : '8px',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      borderRadius: isMobile ? '8px' : '12px'
                    }}>
                      <Lock size={isMobile ? 16 : 20} style={{ color: 'white' }} />
                    </div>
                    <div>
                      <h3 style={{
                        margin: 0,
                        fontSize: isMobile ? '14px' : '18px',
                        fontWeight: '700',
                        color: '#f9fafb'
                      }}>
                        Privacy Controls
                      </h3>
                      <p style={{
                        margin: 0,
                        fontSize: isMobile ? '10px' : '12px',
                        color: '#9ca3af'
                      }}>
                        Control what information is visible to other users
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '12px' : '16px' }}>
                    {[
                      { 
                        key: 'isPublic', 
                        label: 'Public Profile', 
                        description: 'Allow others to find and view your profile',
                        icon: Globe,
                        value: isPublic
                      },
                      { 
                        key: 'showLocation', 
                        label: 'Show Location', 
                        description: 'Display your location on your profile',
                        icon: MapPin,
                        value: showLocation
                      },
                      { 
                        key: 'showMedicalStatus', 
                        label: 'Show Medical Status', 
                        description: 'Display if you\'re a medical user',
                        icon: Plus,
                        value: showMedicalStatus
                      },
                      { 
                        key: 'showConsumptionMethods', 
                        label: 'Show Cannabis Preferences', 
                        description: 'Display your strains and consumption methods',
                        icon: Leaf,
                        value: showConsumptionMethods
                      }
                    ].map((setting) => (
                      <div
                        key={setting.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: isMobile ? '12px' : '16px',
                          background: setting.value 
                            ? 'linear-gradient(135deg, #00d4aa10 0%, #10b98110 100%)'
                            : 'linear-gradient(135deg, #37415110 0%, #4b556310 100%)',
                          border: `1px solid ${setting.value ? '#00d4aa40' : '#37415140'}`,
                          borderRadius: isMobile ? '8px' : '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => this.updateField(setting.key, !setting.value)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px' }}>
                          <setting.icon 
                            size={isMobile ? 14 : 18} 
                            style={{ 
                              color: setting.value ? '#00d4aa' : '#6b7280' 
                            }} 
                          />
                          <div>
                            <div style={{
                              color: '#f9fafb',
                              fontSize: isMobile ? '12px' : '14px',
                              fontWeight: '600'
                            }}>
                              {setting.label}
                            </div>
                            <div style={{
                              color: '#9ca3af',
                              fontSize: isMobile ? '10px' : '12px'
                            }}>
                              {setting.description}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{
                          width: isMobile ? '40px' : '48px',
                          height: isMobile ? '20px' : '24px',
                          background: setting.value 
                            ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                            : '#374151',
                          borderRadius: isMobile ? '10px' : '12px',
                          position: 'relative',
                          transition: 'all 0.3s ease'
                        }}>
                          <div style={{
                            width: isMobile ? '16px' : '20px',
                            height: isMobile ? '16px' : '20px',
                            background: 'white',
                            borderRadius: '50%',
                            position: 'absolute',
                            top: '2px',
                            left: setting.value ? (isMobile ? '22px' : '26px') : '2px',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? '16px' : '0',
            marginTop: isMobile ? '20px' : '32px',
            padding: isMobile ? '16px' : '24px',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            borderRadius: isMobile ? '16px' : '20px',
            border: '1px solid #334155'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '12px',
              color: '#94a3b8',
              fontSize: isMobile ? '12px' : '14px',
              order: isMobile ? 2 : 1
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                animation: 'pulse 2s infinite'
              }} />
              <span style={{ fontWeight: '500' }}>
                {isMockProfile ? 'Ready to create your profile?' : 'Changes will be saved when you click Save'}
              </span>
            </div>

            <button
              onClick={this.handleSave}
              disabled={loading}
              style={{
                padding: isMobile ? '12px 24px' : '16px 32px',
                background: loading 
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                  : 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                color: 'white',
                border: 'none',
                borderRadius: isMobile ? '12px' : '16px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '8px' : '12px',
                transition: 'all 0.3s ease',
                boxShadow: loading ? 'none' : '0 8px 32px rgba(0, 212, 170, 0.3)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                order: isMobile ? 1 : 2,
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 12px 40px rgba(0, 212, 170, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 32px rgba(0, 212, 170, 0.3)';
                }
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: isMobile ? '16px' : '20px',
                    height: isMobile ? '16px' : '20px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={isMobile ? 16 : 20} />
                  {isMockProfile ? 'Create Profile' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </div>

        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @media (max-width: 768px) {
              * {
                -webkit-tap-highlight-color: transparent;
              }
              
              input, textarea, button {
                font-size: 16px !important; /* Prevents zoom on iOS */
              }
            }
          `}
        </style>
      </div>
    );
  }
}

export default EditProfile;